import { createFileRoute, Outlet, useRouterState } from '@tanstack/react-router';

import { TeamFrame } from '@/pages/team/team-frame';
import { activeTeamTab } from '@/pages/team/team-tabs';
import { useTeam, useTeams } from '@/shared/api';
import { useMobile } from '@/shared/lib/use-mobile';

/**
 * Layout for `/teams/$teamSlug/*`.
 *
 * Desktop: the team-level pages — the index panels (Overview · Projects ·
 * Members), Docs, Time, Billing — sit under one `TeamFrame` (identity row +
 * tab bar, §6 W5-A), which stays mounted while the tab changes. Project and
 * task pages, and the chat redirect, render bare. The phone page draws its
 * own header and swipeable tabs, so the frame is desktop-only.
 */
function TeamLayout() {
  const isMobile = useMobile();
  const { teamSlug } = Route.useParams();
  // Primitives only, so a router tick that changes neither doesn't re-render.
  const routeId = useRouterState({
    select: (s) => s.matches[s.matches.length - 1]?.routeId as string | undefined,
  });
  const tab = useRouterState({
    select: (s) => (s.matches[s.matches.length - 1]?.search as { tab?: unknown } | undefined)?.tab,
  });
  const team = useTeam(isMobile ? undefined : teamSlug).data;
  const summary = (useTeams().data ?? []).find((t) => t.id === team?.id);
  const active = isMobile
    ? null
    : activeTeamTab(routeId, { tab }, {
        kind: team?.kind ?? 'regular',
        myRolePreset: summary?.myRolePreset ?? null,
      });

  if (!active) return <Outlet />;
  return (
    <TeamFrame teamSlug={teamSlug} active={active}>
      <Outlet />
    </TeamFrame>
  );
}

export const Route = createFileRoute('/teams/$teamSlug')({
  component: TeamLayout,
});
